import { Elements } from '@/components/elements';
import { USE_CASES_EXPLORE_ITEMS, type UseCaseExploreItem } from '@/utils/content/useCasesOverviewCards';
import clsx from 'clsx';
import Image from 'next/image';
import { Link } from 'react-router';

export type UseCasesOverviewCardsProps = {
    items?: UseCaseExploreItem[];
    sectionTitle?: string;
    learnMoreLabel?: string;
    className?: string;
};

const DEFAULT_SECTION_TITLE = 'Use Cases';

export function UseCasesOverviewCards({
    items = USE_CASES_EXPLORE_ITEMS,
    sectionTitle = DEFAULT_SECTION_TITLE,
    learnMoreLabel = 'Learn More',
    className,
}: UseCasesOverviewCardsProps) {
    return (
        <section
            className={clsx(
                'overflow-hidden px-5 py-16 md:px-10 md:py-20 lg:px-[79px]',
                className,
            )}
        >
            <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-10">
                <Elements.Title
                    title={sectionTitle}
                    type="h2"
                    className="text-start text-3xl font-medium! text-gray-900 md:text-4xl"
                />
                <div className="flex flex-col gap-6">
                    {items.map((item) => (
                        <article
                            key={item.slug}
                            className={clsx(
                                'group relative flex flex-col gap-6 overflow-hidden rounded-2xl border border-gray-200/80 bg-brand-50 p-6 shadow-[0px_0px_5px_0px_#00000017,2px_2px_7px_0px_#0000000D] transition-colors duration-200 md:p-8',
                                'hover:border-brand-700 hover:bg-brand-700',
                                'md:flex-row md:items-stretch md:justify-between md:gap-8 md:min-h-0',
                                'lg:gap-10',
                            )}
                        >
                            <Link
                                to={item.href}
                                className="absolute inset-0 z-10 cursor-pointer rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2"
                                aria-label={`${learnMoreLabel}: ${item.title}`}
                            />
                            <div
                                className={clsx(
                                    'pointer-events-none relative z-0 order-2 flex min-h-0 flex-1 flex-col md:order-1',
                                    'md:min-w-0 md:self-stretch',
                                    'lg:max-w-[674px] lg:flex-none',
                                )}
                            >
                                <div className="flex min-h-0 flex-col gap-4 md:flex-1 md:justify-between md:gap-0">
                                    <div className="flex shrink-0 flex-col gap-4">
                                        <Elements.Title
                                            title={item.title}
                                            type="h3"
                                            className="text-left text-xl font-semibold text-gray-900 transition-colors group-hover:text-gray-50 md:text-2xl!"
                                        />
                                        <Elements.Description
                                            description={item.description}
                                            type="medium"
                                            className="text-left text-gray-600 transition-colors group-hover:text-gray-100"
                                        />
                                    </div>
                                    <div className="shrink-0 pt-2">
                                        <span
                                            className={clsx(
                                                'btn btn-primary btn-outline flex w-full cursor-pointer justify-center transition-colors md:inline-flex md:w-auto',
                                            )}
                                        >
                                            {learnMoreLabel}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div
                                className={clsx(
                                    'pointer-events-none relative z-0 order-1 -mx-6 aspect-500/305 w-[calc(100%+3rem)] max-w-none shrink-0 overflow-hidden rounded-xl bg-gray-100',
                                    'md:order-2 md:mx-0 md:h-[300px] md:w-[300px] md:max-w-[300px] md:flex-none md:shrink-0 md:aspect-auto',
                                    'lg:h-[305px] lg:w-[500px] lg:max-w-none',
                                )}
                            >
                                <Image
                                    src={item.image}
                                    alt={item.imageAlt}
                                    fill
                                    className="scale-[1.02] object-cover"
                                    sizes="(min-width: 1024px) 500px, (min-width: 768px) 300px, min(500px, 100vw)"
                                />
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}
